import { cn } from '../lib/utils'
import { Portal } from './Portal'

/**
 * Shown when saveWithConflictCheck detects a concurrent edit.
 * The user must choose: keep their version or accept the server version.
 * Cannot be dismissed without choosing.
 */
export const ConflictModal = ({ localRecord, serverRecord, fieldLabels = {}, onKeepMine, onUseServer }) => {
  const allKeys = [...new Set([
    ...Object.keys(localRecord ?? {}),
    ...Object.keys(serverRecord ?? {}),
  ])].filter((k) => !['id', 'household_id', 'created_at', 'updated_at'].includes(k))

  const conflictingKeys = allKeys.filter(
    (k) => JSON.stringify(localRecord?.[k]) !== JSON.stringify(serverRecord?.[k])
  )

  const displayKeys = conflictingKeys.length > 0 ? conflictingKeys : allKeys.slice(0, 6)

  const fmt = (val) => {
    if (val === null || val === undefined) return <span className="text-text-muted italic">empty</span>
    if (typeof val === 'boolean') return val ? 'Yes' : 'No'
    if (typeof val === 'object') return JSON.stringify(val)
    return String(val)
  }

  return (
    <Portal>
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-surface rounded-base shadow-xl w-full max-w-lg">
        <div className="p-5 border-b border-border">
          <h2 className="text-base font-semibold text-text-primary">Conflict detected</h2>
          <p className="text-sm text-text-secondary mt-1">
            Someone else edited this record while you were working. Choose which version to keep.
          </p>
        </div>

        <div className="p-5 overflow-y-auto max-h-[50vh]">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="text-xs font-semibold text-primary uppercase tracking-wide">Your changes</div>
            <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Current version</div>
          </div>

          {displayKeys.map((key) => {
            const isDifferent = JSON.stringify(localRecord?.[key]) !== JSON.stringify(serverRecord?.[key])
            return (
              <div
                key={key}
                className={cn(
                  'grid grid-cols-2 gap-3 py-2 border-b border-border last:border-0',
                  isDifferent && 'bg-amber-50 -mx-5 px-5 rounded'
                )}
              >
                <div>
                  <div className="text-xs text-text-muted mb-0.5">
                    {fieldLabels[key] ?? key.replace(/_/g, ' ')}
                  </div>
                  <div className="text-sm text-text-primary break-words">{fmt(localRecord?.[key])}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted mb-0.5">&nbsp;</div>
                  <div className={cn('text-sm break-words', isDifferent ? 'text-text-secondary' : 'text-text-primary')}>
                    {fmt(serverRecord?.[key])}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="p-5 border-t border-border flex gap-3">
          <button
            onClick={onUseServer}
            className="flex-1 px-4 py-2.5 rounded-base border border-border text-sm font-medium text-text-secondary hover:bg-background transition-colors"
          >
            Use current version
          </button>
          <button
            onClick={onKeepMine}
            className="flex-1 px-4 py-2.5 rounded-base bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Keep my changes
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}
